import { Form, useActionData, useLoaderData, useNavigation, useRouteLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { ApprovalItem, Change, DriftReport, Review } from "@edm/contracts";
import { Lines, Loading, times } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { isApiError, type WorkspaceApi } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext, sessionContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Button, Callout, Card, FormError, InboxIcon, LinkButton, PageHeader, Skeleton } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

/**
 * Inbox: things someone is waiting on you for.
 *
 * The line against Drafts is whether anything expires. A finding left open, an
 * exception about to lapse, a ruling someone needs — those age. A draft doesn't,
 * which is why it lives somewhere that never nags.
 *
 * Findings are handled in the pull request itself; one shows up here only
 * because nobody resolved it there. Team activity belongs on Pulse, not here:
 * this page holds only what needs an action.
 */

type DriftItem = DriftReport["findings"][number];
type Finding = Review["findings"][number];

const CHANGE_SCAN_LIMIT = 20;

async function fallback<T>(p: Promise<T>, value: T): Promise<T> {
  try {
    return await p;
  } catch {
    return value;
  }
}

export function inboxLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  const me = context.get(sessionContext)!;
  return { inbox: deferred(loadInbox(context.get(apiContext).workspace(ws.id), me.actor.id)) };
}

async function loadInbox(api: WorkspaceApi, meId: string) {
  const [changes, queue, drift] = await Promise.all([
    fallback(api.listChanges(), { items: [] as Change[], nextCursor: null }),
    fallback(api.approvalQueue(), { items: [] as ApprovalItem[], nextCursor: null }),
    fallback(api.driftFindings(), { findings: [] as DriftItem[] } as DriftReport),
  ]);

  // Findings hang off each change, so an inbox means asking the recent ones.
  // Bounded and concurrent: this is a queue, not an audit.
  const recent = changes.items.slice(0, CHANGE_SCAN_LIMIT);
  const reviews = await Promise.all(recent.map((c) => fallback(api.changeReview(c.id), null).then((r) => ({ change: c, review: r }))));

  const open = reviews.flatMap(({ change, review }) =>
    ((review?.findings ?? []) as Finding[])
      .filter((f) => (f.status ?? "suggested") === "suggested")
      .map((finding) => ({ change, finding, component: review?.components?.[0]?.name ?? change.scope ?? change.title })),
  );

  return {
    open,
    // Every write request someone else is waiting on a ruling for: proposals, amendments and retirements.
    pendingRulings: queue.items.filter((it) => it.submittedBy !== meId).length,
    drift: (drift.findings ?? []).filter((f) => (f.confidence ?? 0) >= 0.8).slice(0, 3),
    hasChanges: changes.items.length > 0,
  };
}

export async function inboxAction({ context, request }: ActionFunctionArgs) {
  const ws = context.get(workspaceContext);
  const api = context.get(apiContext).workspace(ws.id);
  const form = await request.formData();
  try {
    await api.resolveFinding(String(form.get("findingId")), "dismiss");
    return { ok: true };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

/** Finding cards: what it is, the badges, the two ways out. */
function InboxItemsSkeleton() {
  return (
    <Loading>
      {times(2).map((i) => (
        <Card key={i} title={<Skeleton width={i ? 220 : 280} />}>
          <Lines count={2} />
          <div className="dt-inline dt-gap-2 dt-align-center">
            <Skeleton shape="badge" />
            <Skeleton shape="badge" width={120} />
            <Skeleton width={140} />
          </div>
          <div className="dt-inline dt-gap-2">
            <Skeleton shape="button" width={132} />
            <Skeleton shape="button" width={112} />
          </div>
        </Card>
      ))}
    </Loading>
  );
}

export function InboxSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("inbox.title")} description={t("inbox.description")} />
      <InboxItemsSkeleton />
    </>
  );
}

export function InboxRoute() {
  const { inbox } = useLoaderData<typeof inboxLoader>();
  const result = useActionData() as { error?: string } | undefined;
  const { t } = useI18n();

  return (
    <>
      <PageHeader title={t("inbox.title")} description={t("inbox.description")} />
      <FormError message={result?.error} />
      <Deferred data={inbox} fallback={<InboxItemsSkeleton />}>
        {(data) => <Inbox {...data} />}
      </Deferred>
    </>
  );
}

function Inbox({ open, pendingRulings, drift, hasChanges }: Awaited<ReturnType<typeof loadInbox>>) {
  const busy = useNavigation().state === "submitting";
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t, tOr } = useI18n();
  const base = `/w/${workspace.slug}`;
  const empty = open.length === 0 && pendingRulings === 0 && drift.length === 0;

  return (
    <>
      {empty ? (
        <EmptyState title={t("inbox.empty.title")} hint={hasChanges ? t("inbox.empty.hint") : t("inbox.empty.setup")} icon={<InboxIcon size={18} />} />
      ) : (
        <>
          {open.map(({ change, finding, component }) => (
            <Card key={finding.id} title={t("inbox.conflict.title", { component })} data-testid="inbox-finding">
              <p>{finding.explanation}</p>
              <div className="dt-inline dt-gap-2">
                <Badge tone={finding.severity === "critical" || finding.severity === "high" ? "danger" : "warning"}>{finding.severity}</Badge>
                <Badge tone={finding.validationMode === "deterministic" ? "success" : "neutral"}>
                  {t(finding.validationMode === "deterministic" ? "finding.verified" : "finding.readFromDescription")}
                </Badge>
                <span className="dt-subtle">{t("finding.meta", { severity: finding.severity, confidence: Math.round((finding.confidence ?? 0) * 100) })}</span>
              </div>
              {(finding.decisionIds?.length || finding.constraintIds?.length) && (
                <p className="dt-subtle">
                  {t("finding.basedOn")} {[...(finding.decisionIds ?? []), ...(finding.constraintIds ?? [])].join(" · ")}
                </p>
              )}
              <div className="dt-inline dt-gap-2">
                <LinkButton to={`${base}/changes/${change.id}`} variant="secondary">
                  {t("inbox.conflict.open")}
                </LinkButton>
                <Form method="post">
                  <input type="hidden" name="findingId" value={finding.id} />
                  <Button type="submit" variant="ghost" loading={busy}>
                    {t("inbox.conflict.falsePositive")}
                  </Button>
                </Form>
              </div>
              <p className="dt-subtle">{t("inbox.conflict.where")}</p>
            </Card>
          ))}

          {pendingRulings > 0 && (
            <Card title={t("inbox.review.title")}>
              <p>{t("inbox.review.body", { count: pendingRulings })}</p>
              <LinkButton to={`${base}/reviews`} variant="primary">
                {t("inbox.review.action")}
              </LinkButton>
            </Card>
          )}

          {drift.length > 0 && (
            <Card title={t("inbox.drift.title")}>
              {drift.map((d) => (
                <Callout key={d.id} tone="warning" title={tOr(`driftKind.${d.kind}`, d.kind)}>
                  {d.explanation}
                </Callout>
              ))}
              <LinkButton to={`${base}/health`} variant="secondary">
                {t("nav.health")}
              </LinkButton>
            </Card>
          )}
        </>
      )}
    </>
  );
}
